import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './BrowseCards.css';
import { TCreditCard } from '../../types/models/CreditCard';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { Icon } from '../../types/shared/Icon';
import { IReviewData, fetchBatchBVData } from '../../foundation/StarArray/StarArray';
import { Section, Row, Col, Title } from '../../importCL';
import withAsyncImport from '../../withAsyncImport';
import { useWidthContext } from '../../providers/Width';
import { normalizeCardData } from '../../utils/cob-utils';

const BrowseCardsFilter = withAsyncImport(() => import('../BrowseCardsFilter/BrowseCardsFilter'));
const CardCompareDrawer = withAsyncImport(() => import('../CardCompareDrawer/CardCompareDrawer'));
const CardSection = withAsyncImport(() => import('../CardSection/CardSection'));

interface IBrowseCardsProps extends MappedComponentProperties {
    cardDetailPath?: string;
    cardComparePath?: string;
    marketingText?: string;
    creditCards?: TCreditCard[];
    filterIcon?: string;
    cardFilters?: ICardFilter[];
    creditStatusIcons?: Record<string, Icon>;
    tooltip?: string;
    useUpdated?: boolean;
    useCoreProducts?: boolean;
}

export interface ICardFilter {
    category: string;
    filterItems?: IFilterItem[];
}

export interface IFilterItem {
    filterName: string;
    type: 'creditLevelFilter' | 'cardDescriptionBulletFilter' | 'creditBenefitFilter';
    filterString: string;
}

export const BrowseCardsEditConfig = {
    emptyLabel: 'Browse Cards',
    isEmpty: (props: PropsWithChildren<IBrowseCardsProps>) => {
        return !props || !props.cardFilters;
    },
};

export type CardCompareType = TCreditCard | null;


const BrowseCards = (props: PropsWithChildren<IBrowseCardsProps>): JSX.Element => {
    const {useCoreProducts = false} = props;
    const { width } = useWidthContext();
    const [cardsToShow, setCardsToShow] = useState<TCreditCard[]>(props.creditCards ?? []);
    const [cardsToCompare, setCardsToCompare] = useState<CardCompareType[]>(new Array(width > 768 || !props.useUpdated ? 3 : 2).fill(null));
    const [filters, setFilters] = useState<IFilterItem[]>([]);
    const [data, setData] = useState<IReviewData[]>([{ productId: '', average: 0, total: 0, overall: 0 }]);
    const [ srAnnouncement, setSrAnnouncement ] = useState('');

    /**
     * Updates cardsToShow state array whenever filters is updated.
     */
    useEffect(() => {
        const filteredCards = getFilteredCards(filters);
        setCardsToShow(filteredCards);
    }, [filters]);

    /**
     * Will filter all the cards passed into props based on the values in the filters state variable.
     * Filters are applied with OR logic.
     * @param filters
     */
    function getFilteredCards(filters: IFilterItem[]) {
        let filteredCards: TCreditCard[] = [];
        let tempFilteredCards: TCreditCard[] = [];
        const filterIndex: number[] = [];

        if (filters.length > 0) {
            tempFilteredCards = props.creditCards!.filter((card) => {
                let filterMatch = false;
                filters.forEach((filter, index: number) => {
                    const creditFilter = normalizeCardData(card, useCoreProducts).creditStatus;
                    if (filter.type === 'creditLevelFilter') {
                        if (creditFilter.toLocaleLowerCase().includes(filter.filterString.toLocaleLowerCase()) || creditFilter === 'Rebuilding-Excellent') {
                            filterMatch = true;
                        }
                        if (!filterIndex.includes(index)) {
                            filterIndex.push(index);
                        }
                    }

                });
                return filterMatch;
            });
        }

        filterIndex.sort().forEach((filtering, index) => {
            filters.splice(filtering - index, 1);
        });

        if (tempFilteredCards.length === 0) {
            tempFilteredCards = props.creditCards ?? [];
        }

        if (filters.length > 0) {
            filteredCards = tempFilteredCards!.filter((card) => {
                let filterMatch = false;
                filters.forEach((filter) => {

                    if (filter.type === 'cardDescriptionBulletFilter') {
                        const cardDescriptionFilter = normalizeCardData(card, useCoreProducts).cardBullets
                        cardDescriptionFilter.forEach((benefit) => {
                            if (benefit.toLocaleLowerCase().includes(filter.filterString.toLocaleLowerCase())) {
                                filterMatch = true;
                            }
                        });
                    }

                    if (filter.type === 'creditBenefitFilter') {
                        const cardBenefitFilter = normalizeCardData(card, useCoreProducts).cardBenefit;
                        if (cardBenefitFilter && cardBenefitFilter !== '' && cardBenefitFilter.toLocaleLowerCase().includes(filter.filterString.toLocaleLowerCase())) {
                            filterMatch = true;
                        }
                    }
                });
                return filterMatch;
            });
        } else {
            return tempFilteredCards;
        }

        return filteredCards;

    }

    /**
     * Remove/Add Credit Card to CardsToCompare.
     * If Card exists in array it will be removed.
     * If Card does not exist and there is less than 3 cards it will be added to the first null index or pushed to the end.
     * If there are already 3 cards it will do nothing.
     * @param card - This is the card to remove or add
     */
    function cardsToCompareCallback(card: TCreditCard) {
        const existingIndex = cardsToCompare.findIndex((c) => c && c.imagePath === card.imagePath);
        const updated: CardCompareType[] = [...cardsToCompare];
        const maxCardsToCompare = width > 768 || !props.useUpdated ? 3 : 2;

        if (existingIndex >= 0) {
            updated[existingIndex] = null;
            setCardsToCompare(updated);
            return;
        }

        for (let i = 0; i < maxCardsToCompare; ++i) {
            if (cardsToCompare[i] === null) {
                updated[i] = card;
                break;
            }
        }

        setCardsToCompare(updated);
        if (updated.filter(item => item != null).length === maxCardsToCompare) {
            setSrAnnouncement(`Maximum of ${maxCardsToCompare} items can be compared. Remove a selected item before adding another`);
        }
    }

    /**
     * Callback to reset all the cards in the filter.
     */
    function resetCardsToCompareCallback() {
        setCardsToCompare(new Array(width > 768 || !props.useUpdated ? 3 : 2).fill(null));
    }

    useEffect(() => {
        const bvProductIds = cardsToShow.reduce((ids: string[], card: TCreditCard) => {
            const bazaarVoiceId = normalizeCardData(card, useCoreProducts).bazaarVoiceId;
            if (bazaarVoiceId) {
                ids.push(bazaarVoiceId);
            }
            return ids;
        }, []) || [];

        fetchBatchBVData(bvProductIds).then(setData);
    }, []);

    useEffect(() => {
        if (props.useUpdated) {
            if (width > 768 && cardsToCompare.length === 2) {
                setCardsToCompare([...cardsToCompare, null]);
            } else if (width <= 768 && cardsToCompare.length === 3) {
                setCardsToCompare(cardsToCompare.slice(0, 2));
            }
        }
    }, [width]);

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [data]);

    return (
        <Section id='browse-cards' className='CardBrowse'>
            {/* Screen reader announcements */}
            <div
                id="compare-status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only">
                    {srAnnouncement}
            </div>
            <Row xs={1} md={2}>
                <Col className='CardBrowse__header'>
                    <Col xs={12} md={12}>
                        <Title tag='h3' className='CardBrowse__header'>
                            {`Showing ${cardsToShow.length} of ${props.creditCards?.length ?? 0} Cards`}
                        </Title>
                    </Col>

                    <Col xs={12} md={12}>
                        {transformHTMLParserLinks(props.marketingText ?? '')}
                    </Col>
                </Col>


                {props.cardFilters &&
                    <Col className = 'CardBrowse__filterContainer'>    
                        <div>                 
                            <BrowseCardsFilter filterItems={props.cardFilters}
                                filterIcon={props.filterIcon}
                                filters={filters}
                                applyFiltersCallback={setFilters}
                                getCardCountCallback={(f: IFilterItem[]) => getFilteredCards(f).length} />
                        </div>   
                    </Col>
                }


            </Row>
            <Row className='CardBrowse__cards'>
                <Col xs={12} md={12}>
                    {cardsToShow && (
                        cardsToShow.map((card, index) => {
                            const bazaarVoiceId = normalizeCardData(card, useCoreProducts).bazaarVoiceId;
                            const reviewData = data.find((d) => d.productId === bazaarVoiceId);
                            return (
                                <CardSection
                                    callBatch={true}
                                    reviewData={reviewData}
                                    key={index}
                                    card={card}
                                    creditStatusIcons={props.creditStatusIcons || {}}
                                    compareCards={cardsToCompare}
                                    compareCardsCallback={cardsToCompareCallback}
                                    useUpdated={props.useUpdated}
                                    useCoreProducts={useCoreProducts}
                                    tooltip={props.tooltip} />
                            );
                        })
                    )}
                    {!cardsToShow.length && <h1>No Cards To Show</h1>}
                </Col>
            </Row>
            <CardCompareDrawer cardsToCompare={cardsToCompare} cardsToCompareCallback={cardsToCompareCallback} useUpdated={props.useUpdated} useCoreProducts={useCoreProducts}
                resetCardsToCompareCallback={resetCardsToCompareCallback} pathToCompare={props.cardComparePath} />
        </Section>
    );
};

export default BrowseCards;
